import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faPencil, faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { EditorInfoContext } from "../../../../../../../utils/userInfoContext";
import CustomTooltip from "../../../../../../Elements/Tooltip";

// Editor-side slide manager shown in the Content tab while Slide show is on.
const LightBoxSlideList = ({ slides, canAdd, adding, onAdd }) => {
    const { setEditElement, setOpenRemoveModal, setOpenAddElement, setToolType } = useContext(EditorInfoContext);
    const ordered = useMemo(() => (
        (slides || [])
            .filter(s => s?.file_url || s?.value?.gif_link)
            .sort((a, b) => (a.order_on_page ?? a.id) - (b.order_on_page ?? b.id) || a.id - b.id)
    ), [slides]);

    // One row of 6; anything beyond scrolls horizontally with arrow controls.
    const trackRef = useRef(null);
    const prevCount = useRef(ordered.length);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    const updateArrows = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 1);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = trackRef.current;
        // Reveal a newly added slide, which lands at the end.
        if (el && ordered.length > prevCount.current) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
        prevCount.current = ordered.length;
        updateArrows();
    }, [ordered.length, updateArrows]);

    useEffect(() => {
        window.addEventListener('resize', updateArrows);
        return () => window.removeEventListener('resize', updateArrows);
    }, [updateArrows]);

    const scrollByPage = (dir) => {
        const el = trackRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: 'smooth' });
    };

    return (
        <div className="lb-slides">
            <div className="lb-slides__header">
                <div className="lb-slides__title">
                    SLIDES <span className="lb-slides__count">{ordered.length}</span>
                </div>
                <button
                    type="button"
                    className="lb-slides__add"
                    onClick={onAdd}
                    disabled={!canAdd || adding}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {adding ? 'Adding…' : 'Add to slideshow'}
                </button>
            </div>
            <p className="lb-slides__hint">
                {canAdd
                    ? 'Image selected. Add it as the next slide, or Save to add it automatically.'
                    : 'Choose an image above, then add it here. Repeat for each slide.'}
            </p>

            {ordered.length === 0 ? (
                <div className="lb-slides__empty">No slides yet</div>
            ) : (
                <div className="lb-slides__viewport">
                {canPrev && (
                    <button type="button" className="lb-slides__arrow lb-slides__arrow--prev" onClick={() => scrollByPage(-1)} aria-label="Previous slides">
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                )}
                <ul className="lb-slides__grid" ref={trackRef} onScroll={updateArrows}>
                    {ordered.map((slide, i) => (
                        <li key={slide.id} className="lb-slides__tile">
                            <img src={slide.file_url ?? slide.value?.gif_link} alt={`Slide ${i + 1}`} />
                            <span className="lb-slides__num">{i + 1}</span>
                            <div className="lb-slides__actions">
                                <CustomTooltip title={<p className="p-1 text-light">Edit slide</p>}>
                                    <button
                                        type="button"
                                        className="lb-slides__icon"
                                        aria-label={`Edit slide ${i + 1}`}
                                        onClick={() => {
                                            setToolType('LightboxesToolbox');
                                            setEditElement(slide);
                                            setOpenAddElement(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                    </button>
                                </CustomTooltip>
                                <CustomTooltip title={<p className="p-1 text-light">Remove slide</p>}>
                                    <button
                                        type="button"
                                        className="lb-slides__icon lb-slides__icon--danger"
                                        aria-label={`Remove slide ${i + 1}`}
                                        onClick={() => {
                                            setEditElement(slide);
                                            setOpenRemoveModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} />
                                    </button>
                                </CustomTooltip>
                            </div>
                        </li>
                    ))}
                </ul>
                {canNext && (
                    <button type="button" className="lb-slides__arrow lb-slides__arrow--next" onClick={() => scrollByPage(1)} aria-label="Next slides">
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                )}
                </div>
            )}
        </div>
    );
};

export default LightBoxSlideList;
